import { API_BASE_URL } from "../utils/apiConfig";
import { refreshTokenOnce } from "./tokenRefresh";
import { tenantEntryStorage } from "./tenantEntryStorage";
import { tokenStorage } from "./tokenStorage";

/**
 * O fio ao vivo de uma conversa.
 *
 * Transporte e nada mais. Sabe de frames e nunca de mensagens: a transcricao vem
 * toda do conversationsApi.get, e este modulo so diz quando vale a pena voltar a
 * pedi-la. O cabecalho do endpoint no backend tem a razao completa.
 *
 * Feito a mao sobre `fetch` e nao com EventSource, pela mesma razao do
 * sse-client.ts do widget: o EventSource nao poe cabecalhos, e este pedido leva
 * dois, o Authorization e o X-Tenant-ID que o interceptor do createApiInstance
 * poe em todos os outros pedidos deste repositorio. Um token na query string ia
 * para os access logs e para o historico do browser.
 *
 * Emite dois estados e nao quatro. O `live` e o `ended` dependem de EVENTOS
 * (o `live` e o `resolved`) e nao do transporte, portanto sao do hook: um
 * transporte aberto contra um backend que ainda nao serve esta rota nao esta
 * vivo, esta a caminho de um 404.
 */

// Quanto tempo um fio tem de durar para que o seu fim conte como churn normal e
// nao como falha. Abaixo disto o backoff escala em vez de reiniciar.
//
// 5s sai de uma propriedade do endpoint e nao de um numero redondo: nada nele
// aceita um fio e o fecha a seguir por sua iniciativa, com uma excepcao
// deliberada que o plano do backend especifica e que ainda nao esta implantada,
// a conversa que nao esta aberta, que respondera `resolved` e acabara em
// milissegundos. Essa nao contara como falha porque o hook chama close() ao
// receber o `resolved`, antes de este ciclo chegar ao backoff. Tudo o mais que
// abra e feche depressa veio da rede pelo meio, um ingress a tamponar ou um read
// timeout de um proxy, e chega igualmente em milissegundos. Um fio que funciona
// dura muito mais do que isto, porque o backend faz ping precisamente para
// sobreviver ao idle timeout de um proxy.
//
// O teto de ligacoes por worker e a pre-verificacao do barramento respondem 503
// ANTES do 200, o que reforca isto ao tirar duas causas de aceitar-e-fechar do
// lado do servidor. Os dois estao especificados no plano do backend e ainda nao
// estao implantados: enquanto nao estiverem, este limite continua correcto pelo
// paragrafo acima, que nao depende deles.
const MIN_HEALTHY_STREAM_MS = 5000;

// Quanto tempo sem UM BYTE antes de dar o fio por morto e reconectar. E esta
// constante que torna segura a decisao de desligar a sondagem enquanto o fio
// serve: sem ela, um socket meio-aberto (sem FIN, que e o que um NAT a expirar
// deixa) deixava o operador a olhar para uma transcricao parada com ar de viva,
// e sem sondagem a corrigi-la.
//
// 70s sao tres heartbeats mais folga. O heartbeat do backend e o
// widget_sse_heartbeat_seconds, 20s por omissao, e e configuravel: se alguem o
// levantar acima de ~23s, isto tem de subir com ele. O acoplamento esta escrito
// aqui porque nao ha nada que o force.
const HEARTBEAT_GRACE_MS = 70000;

// Exponencial com teto e jitter. O teto importa mais do que a curva: um deploy
// rolante acaba todos os fios ao mesmo tempo e um backoff sem teto ou
// estampedeava os pods novos ou deixava os operadores as escuras durante
// minutos.
const defaultBackoff = (attempt) => {
  const base = Math.min(1000 * 2 ** attempt, 30000);
  return base * (0.5 + Math.random() / 2);
};

/**
 * @param {{
 *   conversationId: string,
 *   onEvent: (event: object) => void,
 *   onStatus: (status: "connecting" | "offline") => void,
 *   fetchImpl?: typeof fetch,
 *   backoffMs?: (attempt: number) => number,
 *   heartbeatGraceMs?: number,
 * }} options
 * @returns {{ close: () => void, reconnect: () => void }}
 */
export function openConversationStream({
  conversationId,
  onEvent,
  onStatus,
  fetchImpl,
  backoffMs = defaultBackoff,
  heartbeatGraceMs = HEARTBEAT_GRACE_MS,
}) {
  // Load-bearing: resolvido para uma variavel local e chamado a seco, nunca
  // como um metodo. Uma chamada de metodo liga o receiver ao objecto, e o fetch
  // global do browser lanca "Illegal invocation" para qualquer receiver que nao
  // seja a Window. Foi assim que o rest-client.ts do widget foi para producao
  // com o lado dos pedidos morto e todos os testes verdes.
  const doFetch = fetchImpl || ((...args) => window.fetch(...args));

  let closed = false;
  let attempt = 0;
  let reader = null;
  let watchdog = null;
  let refreshed = false;

  const clearWatchdog = () => {
    if (watchdog) clearTimeout(watchdog);
    watchdog = null;
  };

  // Cancelar o reader e o que faz o ciclo do run() cair para o backoff e
  // reabrir. Nao ha caminho mais curto: o read() nao volta sozinho, nem expira.
  const cancelReader = () => {
    if (reader) reader.cancel().catch(() => {});
  };

  const armWatchdog = () => {
    clearWatchdog();
    watchdog = setTimeout(cancelReader, heartbeatGraceMs);
  };

  const close = () => {
    closed = true;
    clearWatchdog();
    cancelReader();
    reader = null;
  };

  /**
   * Fecha o fio actual e deixa o ciclo reabrir.
   *
   * Existe para quem consome, e nao para uso interno: quando um pedido da
   * transcricao falha com o fio vivo, marcar um estado nao chega. O fio
   * continuaria vivo e a entregar eventos enquanto a sondagem tambem corria, e
   * nada voltaria a por o estado em `live`, porque isso so acontece num `live`
   * novo. Reabrir devolve a maquina de estados ao caminho normal.
   */
  const reconnect = () => {
    clearWatchdog();
    cancelReader();
  };

  const handleFrame = (frame) => {
    // A spec do SSE define os dados de uma frame como o JOIN por newline de
    // todas as suas linhas `data:`, portanto juntam-se e faz-se um parse. Fazer
    // parse linha a linha funciona hoje so porque o _frame do backend usa um
    // json.dumps de uma linha: no dia em que algo indentar, cada linha lanca por
    // si e o evento perde-se atras de N avisos.
    const data = [];
    for (const line of frame.split("\n")) {
      // As linhas que comecam por dois pontos sao o heartbeat, e ignora-las e o
      // ponto todo de o heartbeat ser um comentario.
      if (!line.startsWith("data:")) continue;
      data.push(line.slice(5).trim());
    }
    if (data.length === 0) return;
    try {
      onEvent(JSON.parse(data.join("\n")));
    } catch {
      // Uma frame malformada nao pode acabar o fio. As duas pontas sao nossas,
      // portanto isto e um defeito e nao um caso do protocolo.
      console.warn("dropped an unparseable conversation stream frame");
    }
  };

  const read = async (streamReader) => {
    reader = streamReader;
    const decoder = new TextDecoder();
    // As frames atravessam fronteiras de chunk: o TCP nao respeita \n\n. O que
    // vier depois da ultima linha em branco fica aqui ate o resto chegar.
    let buffer = "";
    try {
      while (!closed) {
        const { done, value } = await streamReader.read();
        if (done) return;
        armWatchdog();
        buffer += decoder.decode(value, { stream: true });
        let boundary = buffer.indexOf("\n\n");
        while (boundary !== -1) {
          handleFrame(buffer.slice(0, boundary));
          buffer = buffer.slice(boundary + 2);
          boundary = buffer.indexOf("\n\n");
        }
      }
    } finally {
      // A saida pelo `while (!closed)` e a que precisa disto, e e uma janela
      // real: um close() que aterre entre o fetch ser emitido e o primeiro
      // read() cancela um reader que ainda e null, e o ciclo cai fora da
      // condicao deixando o corpo da resposta aberto. O browser fica com a
      // ligacao, o gerador do backend nunca desenrola, o
      // request.is_disconnected() fica falso, o finally do subscribe nunca
      // corre e o fio gasta um lugar do teto por worker.
      if (reader === streamReader) reader = null;
      streamReader.cancel().catch(() => {});
    }
  };

  const run = async () => {
    while (!closed) {
      let openedAt = 0;
      onStatus(attempt === 0 ? "connecting" : "offline");
      try {
        const tenantId = tenantEntryStorage.get()?.id;
        const response = await doFetch(
          `${API_BASE_URL}/backoffice/conversations/${conversationId}/events`,
          {
            headers: {
              Authorization: `Bearer ${tokenStorage.getAccessToken()}`,
              Accept: "text/event-stream",
              // O mesmo cabecalho que o interceptor do axios poe. Sem ele, um
              // super admin dentro de um tenant tem a transcricao a funcionar
              // (o axios manda-o) e o fio a dar 404 (o fetch nao mandava), que
              // e um sintoma que nao se le como falta de um cabecalho.
              ...(tenantId ? { "X-Tenant-ID": tenantId } : {}),
            },
          }
        );

        if (response.status === 401) {
          // O interceptor de refresh e do axios e nao chega a um fetch, portanto
          // o refresh faz-se a mao, uma vez por fio, e insistir com o mesmo
          // token era um ciclo quente.
          //
          // Se o refresh falhar, este ramo desiste e mais nada: o
          // clearTokensAndRedirect NAO e chamado pelo refreshTokenOnce, so pelo
          // catch do createRefreshInterceptor. Quem leva a pagina para o login e
          // portanto o proximo pedido do axios, que a sondagem faz de dentro de
          // 10 segundos. Desistir aqui e o correcto: forcar o redirect deste
          // lado tirava ao operador uma pagina que a sondagem ainda podia
          // recuperar se o refresh tivesse falhado por rede.
          if (refreshed) {
            onStatus("offline");
            return;
          }
          refreshed = true;
          try {
            await refreshTokenOnce();
          } catch {
            onStatus("offline");
            return;
          }
          continue;
        }
        // Terminal os dois, com remedios diferentes e nenhum deles aqui. 403 e o
        // papel retirado a meio; 404 e uma conversa que deixou de existir OU um
        // backend antigo que ainda nao serve esta rota. Nos dois casos a
        // sondagem assume, e e ela que vai dizer a verdade sobre a conversa: um
        // 404 AQUI nunca pode virar "conversa nao encontrada" no ecra.
        //
        // NAO transformar este 404 num retry, e a razao nao e obvia. A retencao
        // que o plano da fase 3.7 (T7) especifica, e que ainda nao esta
        // implantada, vai APAGAR a linha da conversa em vez de lhe mudar o
        // status, e a populacao dela sera exactamente conversas sem actividade
        // durante a janela inteira, que e o que um operador abre quando percorre
        // o historico. O guarda do `mayBeLive` no hook ramifica no `status`, e
        // uma conversa apagada nao tem status nenhum para ler: e este ramo
        // terminal que impede o fio de ficar aberto num canal que e por chat e
        // contacto, a receber a conversa seguinte do mesmo contacto. Um retry
        // aqui reabria essa fuga pela terceira porta.
        //
        // Fecha a fuga mas nao a fecha DEPRESSA, e a diferenca importa. A
        // descoberta precisa de um gatilho, e com o fio `live` a sondagem esta
        // desligada (menos enquanto uma mensagem do cliente espera pela ponte de
        // handoff, ver o `awaitingBridge` no ConversationDetail), portanto um fio
        // parado numa linha apagada fica aberto ate
        // algo chegar ao canal ou ate o fio cair por outra razao (deploy
        // rolante, watchdog, rede). A populacao dela sera por construcao
        // contactos que nao escrevem, portanto "nao chega nada" e o caso
        // provavel. O que isso custa e um lugar e um socket, e nao conteudo
        // errado: se o contacto voltar, o eco da mensagem dele provoca a leitura
        // que fecha isto, e o `typing` da conversa nova aparece o tempo de um
        // sub-segundo ate o `onStatus("offline")` o limpar. Este ramo terminal
        // nao depende disso: uma linha que desapareca por qualquer razao da 404,
        // e uma que nunca desapareca nao chega aqui.
        if (response.status === 403 || response.status === 404) {
          onStatus("offline");
          return;
        }
        if (!response.ok || !response.body) {
          throw new Error(`stream failed with ${response.status}`);
        }

        openedAt = Date.now();
        refreshed = false;
        armWatchdog();
        await read(response.body.getReader());
      } catch {
        // Qualquer falha de transporte e uma reconexao e nunca um erro no ecra:
        // a conversa esta inteira no servidor e o pedido da proxima ligacao
        // restaura a vista.
      } finally {
        clearWatchdog();
      }

      if (closed) return;
      onStatus("offline");
      // Um fio que durou e prova de que o backend serve, portanto o seu fim e
      // churn normal (um deploy rolante, ou um reconnect() pedido por quem
      // consome) e a proxima tentativa parte do chao. Um que abriu e morreu logo
      // nao prova nada, e reiniciar ali prendia o browser a uma tentativa por
      // segundo. E o que limita tambem o pior caso do reconnect(): se o fio abre
      // mas o pedido da transcricao falha sempre, cada volta e mais curta que
      // MIN_HEALTHY e o backoff escala ate ao teto de 30s.
      const lived = openedAt > 0 && Date.now() - openedAt >= MIN_HEALTHY_STREAM_MS;
      if (lived) attempt = 0;
      const wait = lived ? backoffMs(0) : backoffMs(attempt++);
      await new Promise((resolve) => setTimeout(resolve, wait));
    }
  };

  void run();
  return { close, reconnect };
}
